/**
 * Geteilte Label-Farb-Helfer für CRM-Dokumente (Projekte & Favoriten).
 *
 * Eine einzige Quelle für:
 *  - die deterministisch aus dem Label-Text abgeleiteten Label-Farben,
 *  - die wählbaren, persistierten Label-Farben (Palette-ID ⇒ Hex).
 *
 * Hex-Werte 1:1 aus dem Claude-Design (`projekte.css`).
 */

// ── Label-Farben (deterministisch aus dem Label-Text) ───────────────────────────

/** Feste Palette für Label-Punkte (aus `projekte.css`: tag-dot--*). */
const LABEL_PALETTE = [
  '#226df8', // blue
  '#548f25', // green
  '#7b3fdb', // violet
  '#d44062', // rose
  '#d8920a', // amber
  '#0d8c8c', // teal
  '#6b7280', // slate
];

/**
 * Stabiler Hash über den Label-Text → feste Farbe aus der Palette.
 * Gleiches Label ⇒ immer gleiche Farbe, ohne Persistenz im Datenmodell.
 * Fallback, wenn keine Farbe am Label persistiert ist.
 */
export function labelColor(label: string): string {
  let hash = 0;
  for (let i = 0; i < label.length; i++) {
    hash = (hash << 5) - hash + label.charCodeAt(i);
    hash |= 0; // auf 32-bit halten
  }
  const index = Math.abs(hash) % LABEL_PALETTE.length;
  return LABEL_PALETTE[index];
}

// ── Persistierte Label-Farben (Palette-ID ⇒ Hex) ───────────────────────────────

/** Wählbare Label-Farbe (ID = im Datenmodell gespeicherter Wert). */
export type TagColorId =
  | 'blue'
  | 'green'
  | 'violet'
  | 'rose'
  | 'amber'
  | 'teal'
  | 'slate';

/** Reihenfolge wie die Swatch-Reihe im „Labels verwalten"-Sheet (Image #2). */
export const TAG_COLOR_OPTIONS: { id: TagColorId; label: string; hex: string }[] = [
  { id: 'blue', label: 'Blau', hex: '#226df8' },
  { id: 'green', label: 'Grün', hex: '#548f25' },
  { id: 'violet', label: 'Violett', hex: '#7b3fdb' },
  { id: 'rose', label: 'Rosé', hex: '#d44062' },
  { id: 'amber', label: 'Bernstein', hex: '#d8920a' },
  { id: 'teal', label: 'Petrol', hex: '#0d8c8c' },
  { id: 'slate', label: 'Grau', hex: '#6b7280' },
];

const TAG_COLOR_HEX: Record<string, string> = Object.fromEntries(
  TAG_COLOR_OPTIONS.map((c) => [c.id, c.hex]),
);

/**
 * Effektive Label-Farbe: persistierte Palette-Farbe, falls gesetzt und gültig —
 * sonst die deterministisch aus dem Namen abgeleitete Fallback-Farbe.
 */
export function resolveLabelColor(
  colorId: string | null | undefined,
  name: string,
): string {
  if (colorId && TAG_COLOR_HEX[colorId]) return TAG_COLOR_HEX[colorId];
  return labelColor(name);
}
